import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildIndicatorInput,
  InsertIndicatorMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertIndicatorDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type InsertIndicatorTRPCInput = Omit<
  InsertChildIndicatorInput,
  'schedule' | 'Title' | 'Type' | 'ParentId'
> &
  ScheduleTRPCInput & {
    Title: string;
    Type: NonNullable<InsertChildIndicatorInput['Type']>;
    ParentId: string;
  };

export const useInsertIndicator = createMutationHook({
  name: 'insertIndicator',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.indicator.insert.mutationOptions({ onSuccess }),
  // GraphQL codegen makes Title/Type/ParentId optional (InputMaybe),
  // but callers always provide them — cast accordingly
  mapInput: ({ schedule, ...rest }: InsertChildIndicatorInput) =>
    ({
      ...rest,
      ...mapScheduleToTRPC(schedule),
    }) as InsertIndicatorTRPCInput,
  invalidate: (trpc) => [
    trpc.frontend.indicator.register.queryKey(),
    trpc.frontend.indicator.indicatorsByParentId.queryKey(),
    trpc.frontend.control.register.queryKey(),
    trpc.frontend.risk.register.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertIndicatorMutation => ({
    insertChildIndicator: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertIndicatorDocument,
  graphqlVariables: (input: InsertChildIndicatorInput) => ({ object: input }),
  evictFields: ['control', 'risk'],
});
